import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'

import { AlertState } from 'global/AlertState'
import callApi from 'services/api'
import Modal from 'utils/Modal'
import { useNavigate } from 'react-router-dom'
import { useRecoilState, useSetRecoilState } from 'recoil'
import { loadingState } from 'global/LoaderState'
// Import child forms
import FIRForm from './FIRForm'
import DEForm from './DEForm'
import ACRForm from './ACRForm'

import {
  FIR_INITIAL_VALUES,
  FIR_VALIDATION_SCHEMA,
  DE_INITIAL_VALUES,
  DE_VALIDATION_SCHEMA,
  ACR_INITIAL_VALUES,
  ACR_VALIDATION_SCHEMA,
} from './Schema_InitialValues'

// Constants
const DELINQUENCY_TYPES = {
  FIR: 'fir',
  DE: 'de',
  ACR: 'acr',
}

const DELINQUENCY_OPTIONS = [
  { value: '', label: 'Select Delinquency' },
  { value: DELINQUENCY_TYPES.FIR, label: 'Criminal Case' },
  { value: DELINQUENCY_TYPES.DE, label: 'Departmental Action' },
  { value: DELINQUENCY_TYPES.ACR, label: 'Adverse Entry in ACR' },
]

// Helper function to get initial values based on type
const getInitialValues = (type) => {
  switch (type) {
    case DELINQUENCY_TYPES.FIR:
      const firValues = {
        delinquency: DELINQUENCY_TYPES.FIR,
        ...FIR_INITIAL_VALUES,
      }
      return firValues
    case DELINQUENCY_TYPES.DE:
      const deValues = {
        delinquency: DELINQUENCY_TYPES.DE,
        ...DE_INITIAL_VALUES,
      }

      return deValues
    case DELINQUENCY_TYPES.ACR:
      const acrValues = {
        delinquency: DELINQUENCY_TYPES.ACR,
        ...ACR_INITIAL_VALUES,
      }
      return acrValues
    default:
      return { delinquency: '' }
  }
}

// FIXED: Helper function to get validation schema based on type
const getValidationSchema = (type) => {
  const baseSchemaFields = {
    delinquency: Yup.string().required('Please select a delinquency type'),
  }
  switch (type) {
    case DELINQUENCY_TYPES.FIR:
      // Merge base schema with FIR schema
      const firMergedSchema = Yup.object().shape({
        ...baseSchemaFields,
        ...FIR_VALIDATION_SCHEMA.fields,
      })
      return firMergedSchema
    case DELINQUENCY_TYPES.DE:
      const deMergedSchema = Yup.object().shape({
        ...baseSchemaFields,
        ...DE_VALIDATION_SCHEMA.fields,
      })
      return deMergedSchema
    case DELINQUENCY_TYPES.ACR:
      const acrMergedSchema = Yup.object().shape({
        ...baseSchemaFields,
        ...ACR_VALIDATION_SCHEMA.fields,
      })
      return acrMergedSchema
    default:
      return Yup.object().shape(baseSchemaFields)
  }
}

const prepareFormData = (values, editingData) => {
  const formData = new FormData()
  const { delinquency, ...formFields } = values

  const filteredFormFields = Object.fromEntries(
    Object.entries(formFields).filter(
      ([_, v]) => v !== '' && v !== null && v !== undefined
    )
  )

  const payload = {
    typeOfdeliquency: delinquency,
    empId: editingData.id,
    data: filteredFormFields,
  }

  switch (delinquency) {
    case DELINQUENCY_TYPES.FIR:
      formData.append('req_de', JSON.stringify(payload))
      return { formData, endpoint: 'deliquency/' }
    case DELINQUENCY_TYPES.DE:
      formData.append('req_de', JSON.stringify(payload))
      return { formData, endpoint: 'deliquency/' }
    case DELINQUENCY_TYPES.ACR:
      formData.append('req_de', JSON.stringify(payload))
      return { formData, endpoint: 'deliquency/' }
    default:
      throw new Error('Invalid delinquency type')
  }
}

const AddDelinquency = ({ showModal, handleCloseModal, editingData }) => {
  const [selectedType, setSelectedType] = useState('')
  const [, setAlertMessage] = useRecoilState(AlertState)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const loader = useSetRecoilState(loadingState)

  const navigate = useNavigate()
  // Memoized values
  const initialValues = useMemo(
    () => getInitialValues(selectedType),
    [selectedType]
  )
  const validationSchema = useMemo(
    () => getValidationSchema(selectedType),
    [selectedType]
  )

  // Form submission handler
  const handleSubmit = useCallback(
    async (values, { resetForm }) => {
      setLoading(true)
      formik.setTouched(
        {
          delinquency: true,
          ...Object.keys(values).reduce((acc, key) => {
            if (key !== 'delinquency') acc[key] = true
            return acc
          }, {}),
        },
        true
      )

      try {
        const { formData, endpoint } = prepareFormData(values, editingData)

        const response = await callApi(
          endpoint,
          'POST',
          formData,
          false,
          true,
          setAlertMessage,
          navigate,
          null
        )

        if (response?.success) {
          resetForm()
          setSelectedType('')
          handleCloseModal()
        }
      } catch (error) {
        // console.error('🚫 Error submitting form:', error)
        setAlertMessage({
          type: 'error',
          message:
            'An error occurred while submitting the form. Please try again.',
        })
      } finally {
        setLoading(false)
      }
    },
    [setAlertMessage, navigate, handleCloseModal]
  )

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: handleSubmit,
  })

  useEffect(() => {
    formik.setFieldValue('delinquency', selectedType)
  }, [selectedType])

  const handleDelinquencyChange = useCallback(
    (e) => {
      const value = e.target.value

      setSelectedType(value)
      formik.setFieldValue('delinquency', value)
      formik.setFieldTouched('delinquency', true, true) // ✅ Mark as touched immediately
    },
    [formik]
  )

  const renderChildForm = () => {
    switch (selectedType) {
      case DELINQUENCY_TYPES.FIR:
        return <FIRForm formik={formik} />
      case DELINQUENCY_TYPES.DE:
        return <DEForm formik={formik} />
      case DELINQUENCY_TYPES.ACR:
        return <ACRForm formik={formik} />
      default:
        return null
    }
  }
  const handleClose = () => {
    formik.resetForm()
    setSelectedType('')
    handleCloseModal()
  }

  return (
    <Modal
      showModal={showModal}
      handleCloseModal={handleClose}
      title="Add Delinquency"
      modalWidth={{ width: '800px' }}
    >
      <form onSubmit={formik.handleSubmit}>
        {/* Select field */}
        <div className="form-group row mb-3">
          <label className="col-sm-4 col-form-label ">
            Type of Delinquency <span style={{ color: 'red' }}>*</span>
          </label>
          <div className="col-sm-8">
            <select
              className={` text-uppercase form-control ${
                formik.touched.delinquency && formik.errors.delinquency
                  ? 'is-invalid'
                  : ''
              }`}
              name="delinquency"
              value={formik.values.delinquency}
              onChange={handleDelinquencyChange}
              onBlur={formik.handleBlur}
            >
              {DELINQUENCY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {formik.touched.delinquency && formik.errors.delinquency && (
              <div className="invalid-feedback d-block">
                {formik.errors.delinquency}
              </div>
            )}
          </div>
        </div>

        {/* Conditional child forms */}
        {renderChildForm()}

        {/* Buttons */}
        <div className="d-flex justify-content-center mt-4">
          <button
            type="button"
            className="btn btn-secondary me-2"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading || !selectedType}
          >
            {loading ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

export default AddDelinquency
